/**
 * The operations-console vocabulary.
 *
 * ============================================================================
 * WHAT THIS FILE IS FOR, AND THE ONE RULE IT ENFORCES.
 * ============================================================================
 *
 * `data.tsx` holds the table and its parts. This holds the pieces that make a
 * screen read as an operations console rather than a report: the pulse strip,
 * the KPI card that carries its own history, the event stream, the pipeline,
 * the risk row.
 *
 * THE RULE: EVERY COMPONENT HERE TAKES REAL DATA OR RENDERS ITS OWN ABSENCE.
 * None of them will draw a plausible shape when handed nothing — a sparkline
 * with no points renders a flat rule and says so, a pipeline with no rows says
 * so, a pulse with no reading shows a dash. This matters more here than
 * anywhere else in the panel, because these are exactly the components whose
 * job is to look authoritative, and a confident-looking invented number on a
 * governor's screen is the worst output this system can produce.
 *
 * The measurements come from the house console (samkad.uz), read out of the
 * running page: 11 px uppercase labels at 0.22 px tracking, 12 px data rows,
 * cards at 16 px radius on a single 0 4px 16px/8 % elevation, navy #1a365d and
 * gold #c9a227 as the institution's pair.
 */
import type { ReactNode } from 'react';
import { t } from '../i18n/index.js';
import { DistributionStrip, Sparkline } from './charts.jsx';
import { TrendDelta, type Tone } from './data.jsx';

const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-ok',
  warn: 'text-warn',
  crit: 'text-crit',
  info: 'text-info',
  offline: 'text-offline',
  neutral: 'text-ink-100',
};

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------

/**
 * The eyebrow + title + right-hand slot every console screen opens with.
 *
 * The eyebrow is not decoration: it names the SCOPE an operator is looking at
 * («LIVE OPS · NAVOIY SHAHRI») on a panel where the same layout serves a
 * city-wide governor and a mahalla lead whose figures cover one building.
 * Getting that wrong is the difference between a number and a wrong number.
 */
export function PageHeader({
  eyebrow,
  title,
  subtitle,
  right,
}: {
  readonly eyebrow: ReactNode;
  readonly title: string;
  readonly subtitle?: string;
  readonly right?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-micro font-semibold uppercase tracking-wide text-ink-500">
          {eyebrow}
        </div>
        <h1 className="mt-1.5 text-hero font-bold leading-none text-ink-100">{title}</h1>
        {subtitle ? <p className="mt-1.5 text-label text-ink-300">{subtitle}</p> : null}
      </div>
      {right ? <div className="shrink-0 text-right">{right}</div> : null}
    </div>
  );
}

/**
 * A single rotating headline across the top of a screen.
 *
 * It carries the ONE thing that would otherwise need a scroll to find, and it
 * is a link rather than a notice: a ticker an operator cannot act on is an
 * advert for a problem. When there is nothing to say it renders nothing at all
 * rather than «Hammasi yaxshi», because a permanent green banner is furniture
 * people stop reading — and this line has to still work the day it turns red.
 */
export function Ticker({
  label,
  items,
  index,
}: {
  readonly label: string;
  readonly items: readonly { key: string; text: string; tone: Tone }[];
  readonly index: number;
}): React.JSX.Element | null {
  if (items.length === 0) return null;
  const item = items[index % items.length];
  if (!item) return null;
  return (
    <div className="flex animate-settle items-center gap-3 rounded-card border border-hairline bg-panel bg-card px-4 py-2 shadow-card">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE_TEXT[item.tone]}`} style={{ backgroundColor: 'currentColor' }} />
      <span className="shrink-0 text-micro font-semibold uppercase tracking-wide text-ink-500">
        {label}
      </span>
      <span className="min-w-0 flex-1 truncate text-label text-ink-100">{item.text}</span>
      <span className="shrink-0 text-micro tabular-nums text-ink-500">
        {(index % items.length) + 1}/{items.length}
      </span>
    </div>
  );
}

/**
 * The service-health strip: is the platform itself answering.
 *
 * ============================================================================
 * IT REPORTS WHAT THIS BROWSER MEASURED, NOT WHAT A SERVER CLAIMS.
 * ============================================================================
 *
 * The latency is the round trip of the panel's own last poll and the "as of" is
 * that poll's timestamp — so a green strip means "this screen is genuinely
 * talking to the platform", which is the only version of that claim worth
 * putting on a wall. A health endpoint reporting itself healthy tells an
 * operator nothing about the path between them and it, and that path is what
 * breaks.
 */
export function SystemPulse({
  label,
  ok,
  chips,
  series,
  spread,
}: {
  readonly label: string;
  readonly ok: boolean;
  readonly chips: readonly { key: string; label: string; tone: Tone }[];
  /** Values ordered in TIME — the governor's home samples queue depth as it
      polls, and that genuinely has a direction. Drawn as a line. */
  readonly series?: readonly number[];
  /** One value PER KIOSK — the operator's console shows round-trip time across
      the fleet, which has no direction. Drawn as a ranked strip, for the reason
      `KpiCard` documents at length. */
  readonly spread?: readonly number[];
}): React.JSX.Element {
  return (
    <div className="overflow-hidden rounded-card border border-brand-deep bg-brand-deep bg-hero shadow-lift shadow-inner">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-3">
        <span className="flex items-center gap-2 text-micro font-semibold uppercase tracking-wide text-white/70">
          <span
            className={`h-1.5 w-1.5 rounded-full ${ok ? 'animate-live bg-ok' : 'bg-crit'}`}
          />
          {label}
        </span>
        <div className="flex flex-wrap items-center gap-3">
          {chips.map((chip) => (
            <span key={chip.key} className="flex items-center gap-1.5 text-micro tabular-nums text-white/70">
              <span
                className={`h-1.5 w-1.5 rounded-full ${TONE_TEXT[chip.tone]}`}
                style={{ backgroundColor: 'currentColor' }}
              />
              {chip.label}
            </span>
          ))}
        </div>
      </div>
      {/*
        The latency spread, only when there is something to show.

        With no readings at all this drew an empty box, and on a dark strip that
        is a large blank rectangle that reads as a broken chart — which is
        precisely what a fleet of three mostly-offline kiosks produced. Nothing
        is better than a hole: the strip closes up and the chips above still
        carry the state.
      */}
      {series && series.length >= 2 ? (
        <div className="px-5 pb-2 pt-1 text-gold">
          <Sparkline values={series} className="text-gold" />
        </div>
      ) : spread && spread.length > 0 ? (
        <div className="px-5 pb-2 pt-1 text-gold">
          <DistributionStrip values={spread} className="text-gold" />
        </div>
      ) : (
        <div className="px-5 pb-3" />
      )}
    </div>
  );
}

/**
 * A KPI card: label, the number, the change, and either its history or its
 * spread.
 *
 * That combination is the whole point. The number says where things stand, the
 * small chart says how it got there or how it is distributed, and the delta
 * says whether that is normal. A dashboard tile with only the number is the one
 * every product ships and the one nobody can act on.
 *
 * ============================================================================
 * `series` AND `spread` ARE NOT INTERCHANGEABLE. PICK BY WHAT THE DATA IS.
 * ============================================================================
 *
 *   `series` — values ORDERED IN TIME. Drawn as a line, because a line says
 *              «this followed that».
 *   `spread` — one value PER MEMBER (per kiosk, per campaign). Drawn as a
 *              ranked strip, because there is no time in it and a line would
 *              claim there was.
 *
 * Passing a per-kiosk array to `series` is the mistake this pair exists to
 * make hard: it renders a trend out of the API's sort order.
 *
 * `series` may be empty and the card still renders: `Sparkline` draws a flat
 * rule for fewer than two points rather than inventing a shape.
 */
export function KpiCard({
  label,
  value,
  absent,
  series,
  spread,
  delta,
  deltaLabel,
  tone = 'neutral',
  invertDelta = false,
  footerLabel,
  footerValue,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly absent: string;
  readonly series?: readonly number[];
  readonly spread?: readonly number[];
  readonly delta?: number | null;
  readonly deltaLabel?: string;
  readonly tone?: Tone;
  readonly invertDelta?: boolean;
  readonly footerLabel?: string;
  readonly footerValue?: string;
}): React.JSX.Element {
  return (
    <div className="flex animate-settle flex-col rounded-card border border-hairline bg-panel bg-card shadow-card transition-shadow duration-state hover:shadow-lift">
      <div className="flex items-start justify-between gap-3 px-4 pt-3">
        <span className="text-micro font-semibold uppercase tracking-wide text-ink-500">
          {label}
        </span>
        {delta === undefined ? null : (
          <TrendDelta value={delta} invert={invertDelta} suffix="%" />
        )}
      </div>
      <div className="flex items-end justify-between gap-3 px-4 pb-2">
        <p className={`text-hero font-bold leading-none tabular-nums ${TONE_TEXT[tone]}`}>
          {value ?? <span className="text-ink-500">{absent}</span>}
        </p>
        {series ? (
          <div className="w-24 shrink-0">
            <Sparkline values={series} className={TONE_TEXT[tone]} />
          </div>
        ) : spread && spread.length > 0 ? (
          <div className="w-24 shrink-0">
            <DistributionStrip values={spread} className={TONE_TEXT[tone]} />
          </div>
        ) : null}
      </div>
      {footerLabel ? (
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-hairline px-4 py-1.5">
          {/*
            NOT `truncate`. These footers explain what the figure above means —
            «the system gave up, a person must act» — and an explanation cut off
            at «a person must a…» is worse than one on two lines. `leading-tight`
            keeps a wrapped footer from making the card taller than its row.
          */}
          <span className="text-micro uppercase leading-tight tracking-wide text-ink-500">
            {footerLabel}
          </span>
          <span className="shrink-0 text-micro font-semibold tabular-nums text-ink-100">
            {footerValue ?? deltaLabel}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The tab strip that carries its own counts.
 *
 * The count is what makes it a control rather than a label: an operator picks
 * «Ochiq · 12» over «Hammasi · 340» because the number told them where the work
 * is. A zero is rendered, never hidden — «Nosoz · 0» is the most useful reading
 * on the strip and the one a filtered-out tab would conceal.
 */
export function TabStrip<Value extends string>({
  tabs,
  value,
  onChange,
}: {
  readonly tabs: readonly { value: Value; label: string; count?: number | null }[];
  readonly value: Value;
  readonly onChange: (value: Value) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          aria-pressed={tab.value === value}
          className={`flex items-center gap-2 rounded-card border px-3.5 py-2 text-label transition-colors duration-state ${
            tab.value === value
              ? 'border-brand bg-brand text-white'
              : 'border-hairline bg-panel text-ink-300 hover:bg-panel-raised'
          }`}
        >
          <span className="font-medium">{tab.label}</span>
          {tab.count === undefined || tab.count === null ? null : (
            <span
              className={`rounded px-1.5 text-micro font-semibold tabular-nums ${
                tab.value === value ? 'bg-white/20 text-white' : 'bg-panel-raised text-ink-500'
              }`}
            >
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Big clickable counts that are also the filter — the house pattern.
 *
 * Two jobs in one control, which is right here because they are the same
 * question: "how many are waiting" and "show me the ones waiting" are asked in
 * the same breath, and splitting them into a figure and a dropdown makes the
 * operator state the same intent twice.
 */
export function CountCards<Value extends string>({
  cards,
  value,
  onChange,
}: {
  readonly cards: readonly { value: Value; label: string; count: number; tone?: Tone }[];
  readonly value: Value;
  readonly onChange: (value: Value) => void;
}): React.JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <button
          key={card.value}
          type="button"
          onClick={() => onChange(card.value)}
          aria-pressed={card.value === value}
          className={`animate-settle rounded-card border bg-card px-4 py-3 text-left shadow-card transition-shadow duration-state hover:shadow-lift ${
            card.value === value
              ? 'border-gold bg-panel'
              : 'border-hairline bg-panel hover:bg-panel-raised'
          }`}
        >
          <p className="text-micro font-semibold uppercase tracking-wide text-ink-500">
            {card.label}
          </p>
          <p className={`mt-1 text-figure font-bold tabular-nums leading-none ${TONE_TEXT[card.tone ?? 'neutral']}`}>
            {card.count}
          </p>
        </button>
      ))}
    </div>
  );
}

/**
 * One row of a live stream: dot, headline, context, actor, age.
 *
 * The age sits right-aligned and in tabular figures so a column of them scans
 * as a column. Everything else is left, because the eye reads the headline and
 * only then asks when.
 */
export function StreamRow({
  tone = 'neutral',
  title,
  detail,
  actor,
  age,
}: {
  readonly tone?: Tone;
  readonly title: string;
  readonly detail?: string;
  readonly actor?: string;
  readonly age: string;
}): React.JSX.Element {
  return (
    <li className="flex gap-3 border-b border-hairline px-4 py-2.5 last:border-0">
      <span
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TONE_TEXT[tone]}`}
        style={{ backgroundColor: 'currentColor' }}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-label font-semibold text-ink-100">{title}</p>
        {detail || actor ? (
          <p className="truncate text-micro text-ink-500">
            {detail}
            {detail && actor ? ' · ' : ''}
            {actor ? <span className="font-medium text-ink-300">{actor}</span> : null}
          </p>
        ) : null}
      </div>
      <span className="shrink-0 text-micro tabular-nums text-ink-500">{age}</span>
    </li>
  );
}

/**
 * A ranked risk entity with its score and the actions that close it.
 *
 * ============================================================================
 * THE SCORE IS SHOWN WITH ITS REASON, ALWAYS. THAT IS NOT NEGOTIABLE.
 * ============================================================================
 *
 * A number like «88» beside a name is an accusation an operator cannot check.
 * This component will not render one without `reason`, because the difference
 * between a risk console and a denunciation machine is whether the person
 * looking at it can see WHY — and act on the reason rather than the number.
 */
export function RiskRow({
  rank,
  kind,
  title,
  context,
  reason,
  score,
  tone,
  actions,
}: {
  readonly rank: number;
  readonly kind: string;
  readonly title: string;
  readonly context?: string;
  readonly reason: string;
  readonly score: number;
  readonly tone: Tone;
  readonly actions?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-hairline px-4 py-3 last:border-0">
      <span className="w-8 shrink-0 text-micro tabular-nums text-ink-500">#{rank}</span>
      <div className="min-w-0 flex-1">
        <p className="text-micro font-semibold uppercase tracking-wide text-ink-500">{kind}</p>
        <p className="truncate text-label font-semibold text-ink-100">{title}</p>
        {context ? <p className="truncate text-micro text-ink-500">{context}</p> : null}
        <p className={`mt-0.5 truncate text-micro ${TONE_TEXT[tone]}`}>{reason}</p>
      </div>
      <span className={`shrink-0 text-figure font-bold tabular-nums leading-none ${TONE_TEXT[tone]}`}>
        {score}
      </span>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * A stage in a flow, sized by its share of the whole.
 *
 * §The pipeline this belongs to shows where work is PILING UP, which a set of
 * four equal boxes cannot: proportion is the entire message. The label carries
 * the count so the reader never has to estimate from the height — the height is
 * for the glance, the number is for the decision.
 */
export function PipelineStage({
  label,
  count,
  total,
  tone = 'neutral',
}: {
  readonly label: string;
  readonly count: number;
  readonly total: number;
  readonly tone?: Tone;
}): React.JSX.Element {
  const share = total > 0 ? Math.max(6, (count / total) * 100) : 6;
  return (
    /*
      `h-full` IS LOAD-BEARING. Without it this wrapper has an auto height, so
      the bar's percentage height had nothing to resolve against and every stage
      fell back to its `minHeight` — three offline and one critical rendered as
      four identical bars, which is the exact misreading a proportional chart
      exists to prevent. Percentages need a definite parent height; the flex
      row above supplies one only if this box takes it.
    */
    <div className="flex h-full min-w-0 flex-1 flex-col">
      {/*
        The plot area is `flex-1` — a definite height, which is what the bar's
        percentage resolves against — and the label is BELOW it rather than
        inside the same percentage-bearing box. With the label inside, a single
        status holding the whole fleet gave `share: 100` and pushed its own
        caption off the bottom of the panel.
      */}
      <div className="flex min-h-0 flex-1 items-end">
        <div
          className={`flex w-full items-end justify-center rounded-t ${TONE_TEXT[tone]}`}
          style={{ height: `${share}%`, minHeight: '2rem', backgroundColor: 'currentColor' }}
        >
          <span className="pb-1 text-lead font-bold tabular-nums text-white">{count}</span>
        </div>
      </div>
      <p className="mt-1.5 shrink-0 truncate text-center text-micro uppercase tracking-wide text-ink-500">
        {label}
      </p>
    </div>
  );
}

/** The keyboard-hint line the house console closes every page with. */
export function FooterHint({ text }: { readonly text: string }): React.JSX.Element {
  return <p className="py-4 text-center text-micro text-ink-500">{text}</p>;
}

/**
 * A labelled distribution — the five breakdowns the analytics API returns.
 *
 * Bars rather than a pie: these are categories of very different magnitude
 * (`unclassified` is often one row against thousands), and a pie makes the
 * small ones unreadable at exactly the moment somebody needs to notice one.
 */
export function Distribution({
  title,
  rows,
}: {
  readonly title: string;
  readonly rows: readonly { key: string; label: string; value: number }[];
}): React.JSX.Element {
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  return (
    <div>
      <p className="text-micro font-semibold uppercase tracking-wide text-ink-500">{title}</p>
      <div className="mt-2 space-y-1.5">
        {rows.length === 0 || total === 0 ? (
          <p className="text-micro text-ink-500">{t('app.notYet')}</p>
        ) : (
          rows.map((row) => (
            <div key={row.key} className="flex items-center gap-2">
              <span className="w-24 shrink-0 truncate text-micro text-ink-300">{row.label}</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-hairline">
                <div
                  className="h-full rounded-full bg-brand"
                  style={{ width: `${(row.value / total) * 100}%` }}
                />
              </div>
              <span className="w-12 shrink-0 text-right text-micro tabular-nums text-ink-100">
                {row.value}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
